import React, { useState, useEffect, FC, ReactElement } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { collection, query, getDocs, doc, getDoc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import './Assignment.css';
import { EmailConfig } from '../../types/email';
import { Modal, Button, Form } from 'react-bootstrap';
import { format } from 'date-fns';
import { Registration } from '../../types/registration';
import { useLogger } from '../../utils/logger';
import { FinancialRecord, Payment, OtherFee } from '../../types/financial';
import { computeRegistrationBalance } from '../../utils/invoiceModel';

// Add keyframe animation for spinner
const spinnerAnimation = `
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
`;

// Inject the CSS
const styleElement = document.createElement('style');
styleElement.type = 'text/css';
styleElement.appendChild(document.createTextNode(spinnerAnimation));
document.head.appendChild(styleElement);

interface Accommodation {
  id: string;
  placeName: string;
  placeCode: string;
  type: string;
  status: string;
  priceCode: string;
  placeType: string;
}

type SortField = 'type' | 'name' | 'accommodation' | 'campers' | 'date' | 'balance';
type SortDirection = 'asc' | 'desc';

interface FilterOptions {
  year: number;
  accommodation: string;
}

// Format currency helper
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD'
  }).format(amount);
};

const Assignment: FC = (): ReactElement => {
  // Logger hook
  const logger = useLogger('Assignment');
  
  // Initialize navigate hook and search params
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const savedViewState = (() => {
    try {
      const raw = sessionStorage.getItem('assignmentViewState');
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  })() as Record<string, string>;
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') ?? savedViewState.q ?? '');
  const [statusFilter, setStatusFilter] = useState<string>(searchParams.get('status') ?? savedViewState.status ?? 'all');
  const [typeFilter, setTypeFilter] = useState<string>(searchParams.get('type') ?? savedViewState.type ?? 'all');
  const initialSortField = ((searchParams.get('sortField') ?? savedViewState.sortField) as SortField) || 'date';
  const initialSortDirection = ((searchParams.get('sortDirection') ?? savedViewState.sortDirection) as SortDirection) || 'desc';
  const [sortField, setSortField] = useState<SortField>(initialSortField);
  const [sortDirection, setSortDirection] = useState<SortDirection>(initialSortDirection);

  // Archive view state
  const [viewYear, setViewYear] = useState<string>(searchParams.get('view') ?? savedViewState.view ?? 'current');
  const [availableArchiveYears, setAvailableArchiveYears] = useState<number[]>([]);
  const isArchiveView = viewYear !== 'current';
  const [accommodations, setAccommodations] = useState<Accommodation[]>([]);
  const [selectedAccommodation, setSelectedAccommodation] = useState<string>('');
  const [placeNameDisplay, setPlaceNameDisplay] = useState<string>('');
  const [dormRoomType, setDormRoomType] = useState<'single' | 'double'>('single');
  // Full camp settings (settings/camp) used to compute live balances, matching
  // the Registration Details page and Accounts Receivable report.
  const [campSettings, setCampSettings] = useState<any>(null);
  const [settings, setSettings] = useState<{
    rates: {
      registration: {
        adult: {
          nightly: number;
          weekly: number;
        };
        adultStudent: {
          nightly: number;
          weekly: number;
        };
        youthAndChildren: {
          nightly: number;
          weekly: number;
        };
      };
      accommodation: {
        apartments: {
          [key: string]: {
            nightly: number;
            weekly: number;
          };
        };
        cottages: {
          [key: string]: {
            nightly: number;
            weekly: number;
          };
        };
        dorm?: {
          [key: string]: {
            nightly: number;
            weekly: number;
          };
        };
        campsite?: {
          [key: string]: {
            nightly: number;
            weekly: number;
          };
        };
      };
    };
  }>({
    rates: {
      registration: {
        adult: {
          nightly: 0,
          weekly: 0
        },
        adultStudent: {
          nightly: 0,
          weekly: 0
        },
        youthAndChildren: {
          nightly: 0,
          weekly: 0
        }
      },
      accommodation: {
        apartments: {},
        cottages: {},
        dorm: {},
        campsite: {}
      }
    }
  });

  // Financial data state
  const [financialRecord, setFinancialRecord] = useState<FinancialRecord | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [otherFees, setOtherFees] = useState<OtherFee[]>([]);
  const [showFinancialModal, setShowFinancialModal] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [registrationToDelete, setRegistrationToDelete] = useState<Registration | null>(null);
  const [selectedRegistration, setSelectedRegistration] = useState<Registration | null>(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailConfig, setEmailConfig] = useState<EmailConfig>({
    to: [],
    subject: '',
    html: ''
  });

  // Filter and search state
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    year: new Date().getFullYear(),
    accommodation: 'all'
  });

  // Keep assignment list filters in URL so returning from details restores state.
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set('q', searchTerm);
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (typeFilter !== 'all') params.set('type', typeFilter);
    if (viewYear !== 'current') params.set('view', viewYear);
    if (sortField !== 'date') params.set('sortField', sortField);
    if (sortDirection !== 'desc') params.set('sortDirection', sortDirection);
    setSearchParams(params, { replace: true });
  }, [searchTerm, statusFilter, typeFilter, viewYear, sortField, sortDirection, setSearchParams]);

  // Persist current assignment list view state as a fallback when returnTo URL is unavailable.
  useEffect(() => {
    try {
      sessionStorage.setItem('assignmentViewState', JSON.stringify({
        q: searchTerm,
        status: statusFilter,
        type: typeFilter,
        view: viewYear,
        sortField,
        sortDirection
      }));
    } catch {
      // Ignore storage errors (private mode, quota, etc.)
    }
  }, [searchTerm, statusFilter, typeFilter, viewYear, sortField, sortDirection]);

  // Load settings on component mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settingsRef = doc(db, 'settings', 'rates');
        const settingsDoc = await getDoc(settingsRef);
        if (settingsDoc.exists()) {
          setSettings(settingsDoc.data() as any);
        }
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    };

    loadSettings();
  }, []);

  // Load full camp settings (settings/camp) for live balance calculation
  useEffect(() => {
    const loadCampSettings = async () => {
      try {
        const campSettingsDoc = await getDoc(doc(db, 'settings', 'camp'));
        if (campSettingsDoc.exists()) {
          setCampSettings(campSettingsDoc.data() as any);
        }
      } catch (error) {
        console.error('Error loading camp settings:', error);
      }
    };

    loadCampSettings();
  }, []);

  // Load available archive years
  useEffect(() => {
    const loadArchiveYears = async () => {
      try {
        const archiveSnapshot = await getDocs(collection(db, 'archive'));
        const years = archiveSnapshot.docs
          .map(d => parseInt(d.id))
          .filter(year => !isNaN(year))
          .sort((a, b) => b - a);
        setAvailableArchiveYears(years);
      } catch (error) {
        console.error('Error loading archive years:', error);
      }
    };

    loadArchiveYears();
  }, []);

  // Load accommodations on component mount
  useEffect(() => {
    const loadAccommodations = async () => {
      try {
        const accommodationsRef = collection(db, 'accommodations');
        const accommodationsSnapshot = await getDocs(accommodationsRef);
        const accommodationsData = accommodationsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Accommodation[];
        setAccommodations(accommodationsData);
      } catch (error) {
        console.error('Error loading accommodations:', error);
      }
    };

    loadAccommodations();
  }, []);

  // Load registrations — from live collection or archive depending on viewYear
  useEffect(() => {
    const loadRegistrations = async () => {
      try {
        setLoading(true);
        logger.startProcess('Loading Registrations');
        
        const registrationsRef = viewYear === 'current'
          ? collection(db, 'Registrations')
          : collection(db, 'archive', viewYear, 'registrations');
        const registrationsQuery = query(registrationsRef);
        
        const registrationsSnapshot = await getDocs(registrationsQuery);
        const registrationsData = registrationsSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            firstName: data.registrationInfo?.primaryCamperName?.split(' ')[0] || '',
            lastName: data.registrationInfo?.primaryCamperName?.split(' ').slice(1).join(' ') || '',
            email: data.registrationInfo?.email || '',
            status: data.status || 'unknown',
            accommodationType: data.accommodation?.firstChoice || data.financial?.accommodationFees?.placeName || 'Not specified',
            type: (() => {
              const policy = data.accommodation?.policy || '';
              const policyToType: { [key: string]: number } = {
                'program-staff': 1,
                'returning-same-spot': 2,
                'returning-no-fixed-address': 3,
                'returning-different-accommodations': 4,
                'new-camper': 5,
                'private-cottage-owner': 6,
                'off-grounds': 7
              };
              return policyToType[policy] || 0;
            })(),
            primaryCamperId: data.registrationInfo?.primaryCamperId || '',
            requestedAccommodation: data.accommodation?.firstChoice || data.financial?.accommodationFees?.placeName || 'Not specified',
            numCampers: data.attendingCampers?.length || 0,
            attendingCampers: (data.attendingCampers || []).map((camper: any) => ({
              firstName: camper.firstName || '',
              lastName: camper.lastName || '',
              category: camper.age || 'Adult',
              numberOfNights: 0
            })),
            assignedAccommodation: data.assignedAccommodation || (data.financial?.accommodationFees?.placeName ? {
              placeCode: data.financial.accommodationFees.assignedAccommodation || data.financial.accommodationFees.priceCode || '',
              placeName: data.financial.accommodationFees.placeName || '',
              assignedAccommodation: data.financial.accommodationFees.assignedAccommodation || '',
              description: data.financial.accommodationFees.placeName || '',
              amount: data.financial.accommodationFees.accommodationFee || 0
            } : undefined),
            submittedAt: data.registrationInfo?.submittedAt?.toDate() || new Date(),
            stayDetails: {
              numberOfNights: 0,
              startDate: data.campDates?.arrivalDate || '',
              endDate: data.campDates?.departureDate || ''
            },
            notes: data.accommodation?.notes || '',
            optionalFees: {
              fridgeRental: false,
              donation: 0
            },
            camperNotes: [],
            originalData: data
          } as Registration;
        }) as Registration[];
        
        setRegistrations(registrationsData);
        logger.info(`Loaded ${registrationsData.length} registrations from ${viewYear === 'current' ? 'live' : viewYear + ' archive'}`);
        logger.endProcess();
      } catch (error) {
        console.error('Error loading registrations:', error);
        logger.error('Failed to load registrations', error);
      } finally {
        setLoading(false);
      }
    };

    loadRegistrations();
  }, [viewYear]);

  // Filter and sort registrations
  const filteredAndSortedRegistrations = registrations
    .filter(registration => {
      // Search term filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return (
          (registration.firstName || '').toLowerCase().includes(searchLower) ||
          (registration.lastName || '').toLowerCase().includes(searchLower) ||
          (registration.email || '').toLowerCase().includes(searchLower) ||
          (registration.accommodationType || '').toLowerCase().includes(searchLower)
        );
      }

      // Status filter
      if (statusFilter !== 'all') {
        return (registration.status || '').toLowerCase() === statusFilter.toLowerCase();
      }

      // Type filter
      if (typeFilter !== 'all') {
        return (registration.type || 2).toString() === typeFilter;
      }

      // Year filter
      if (filterOptions.year) {
        const registrationYear = new Date(registration.submittedAt || new Date()).getFullYear();
        return registrationYear === filterOptions.year;
      }

      // Accommodation filter
      if (filterOptions.accommodation !== 'all') {
        return (registration.accommodationType || '') === filterOptions.accommodation;
      }

      return true;
    })
    .sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'type':
          aValue = a.type;
          bValue = b.type;
          break;
        case 'name':
          aValue = `${a.firstName} ${a.lastName}`;
          bValue = `${b.firstName} ${b.lastName}`;
          break;
        case 'accommodation':
          aValue = a.accommodationType;
          bValue = b.accommodationType;
          break;
        case 'campers':
          aValue = a.attendingCampers.length;
          bValue = b.attendingCampers.length;
          break;
        case 'date':
          aValue = new Date(a.submittedAt || new Date());
          bValue = new Date(b.submittedAt || new Date());
          break;
        case 'balance':
          aValue = a.status?.toLowerCase() === 'pending'
            ? 0
            : computeRegistrationBalance((a as any).originalData || a, campSettings);
          bValue = b.status?.toLowerCase() === 'pending'
            ? 0
            : computeRegistrationBalance((b as any).originalData || b, campSettings);
          break;
        default:
          aValue = a.submittedAt || new Date();
          bValue = b.submittedAt || new Date();
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  // Handle registration selection for accommodation assignment
  const handleRegistrationSelect = (registration: Registration) => {
    setSelectedRegistration(registration);
    setShowAssignmentModal(true);
  };

  // Handle viewing invoice
  // Handle accommodation assignment
  const handleAssignAccommodation = async () => {
    if (!selectedRegistration || !selectedAccommodation) {
      return;
    }

    try {
      logger.startProcess('Accommodation Assignment');
      logger.info('Starting accommodation assignment with:', {
        registrationId: selectedRegistration.id,
        selectedAccommodation
      });

      // Find the accommodation details
      const accommodation = accommodations.find(acc => 
        acc.placeCode.toLowerCase() === selectedAccommodation.toLowerCase()
      );

      if (!accommodation) {
        logger.error('Accommodation not found');
        logger.endProcess();
        return;
      }

      logger.debug('Found accommodation:', accommodation);

      // Check for existing assignments with this accommodation code.
      const existingRegistrations = registrations.filter((reg) => {
        if (reg.id === selectedRegistration.id) return false;
        if ((reg.status || '').toLowerCase() === 'cancelled') return false;

        const assignedCode = String(
          reg.assignedAccommodation?.assignedAccommodation ||
          reg.assignedAccommodation?.placeCode ||
          (reg as any).originalData?.financial?.accommodationFees?.assignedAccommodation ||
          ''
        ).trim().toLowerCase();

        return assignedCode === String(accommodation.placeCode || '').trim().toLowerCase();
      });
      
      if (existingRegistrations.length > 0) {
        const existingRegistration = existingRegistrations[0];
        const alertMessage = `Warning: This accommodation (${accommodation.placeCode}) is already assigned to ${existingRegistration.firstName} ${existingRegistration.lastName}.\n\nDo you want to proceed with the assignment?`;
        
        if (!window.confirm(alertMessage)) {
          logger.debug('Assignment cancelled by user');
          logger.endProcess();
          return;
        }
      }

      // Calculate number of nights
      const numberOfNights = selectedRegistration.stayDetails?.numberOfNights || 0;

      // Calculate the accommodation fee
      const accommodationFee = getAccommodationFee(accommodation, numberOfNights);

      // Update the registration with assigned accommodation
      const registrationRef = doc(db, 'registrations', selectedRegistration.id);
      await updateDoc(registrationRef, {
        assignedAccommodation: {
          placeCode: accommodation.placeCode,
          placeName: accommodation.placeName,
          assignedAccommodation: accommodation.type,
          description: accommodation.placeName,
          amount: accommodationFee
        },
        status: 'assigned',
        updatedAt: serverTimestamp()
      });

      // Update local state
      setRegistrations(prev => 
        prev.map(reg => 
          reg.id === selectedRegistration.id 
            ? {
                ...reg,
                assignedAccommodation: {
                  placeCode: accommodation.placeCode,
                  placeName: accommodation.placeName,
                  assignedAccommodation: accommodation.type,
                  description: accommodation.placeName,
                  amount: accommodationFee
                },
                status: 'assigned'
              }
            : reg
        )
      );

      logger.info('Accommodation assigned successfully');
      logger.endProcess();

      // Close modal and reset selection
      setShowAssignmentModal(false);
      setSelectedAccommodation('');
      setPlaceNameDisplay('');
      setSelectedRegistration(null);

    } catch (error) {
      console.error('Error assigning accommodation:', error);
      logger.error('Failed to assign accommodation', error);
    }
  };

  // Calculate accommodation fee based on type and nights
  const getAccommodationFee = (accommodation: Accommodation, numberOfNights: number): number => {
    const accommodationRates = settings.rates.accommodation;
    let rate = 0;

    switch (accommodation.type) {
      case 'Apartment':
        if (accommodationRates.apartments[accommodation.priceCode]) {
          rate = numberOfNights >= 7 
            ? accommodationRates.apartments[accommodation.priceCode].weekly
            : accommodationRates.apartments[accommodation.priceCode].nightly;
        }
        break;
      case 'Cottage':
        if (accommodationRates.cottages[accommodation.priceCode]) {
          rate = numberOfNights >= 7 
            ? accommodationRates.cottages[accommodation.priceCode].weekly
            : accommodationRates.cottages[accommodation.priceCode].nightly;
        }
        break;
      case 'Dorm':
        if (accommodationRates.dorm?.[accommodation.priceCode]) {
          rate = numberOfNights >= 7 
            ? accommodationRates.dorm[accommodation.priceCode].weekly
            : accommodationRates.dorm[accommodation.priceCode].nightly;
        }
        break;
      case 'Campsite':
        if (accommodationRates.campsite?.[accommodation.priceCode]) {
          rate = numberOfNights >= 7 
            ? accommodationRates.campsite[accommodation.priceCode].weekly
            : accommodationRates.campsite[accommodation.priceCode].nightly;
        }
        break;
    }

    return rate * numberOfNights;
  };

  // Handle status updates
  const handleStatusUpdate = async (registrationId: string, newStatus: string) => {
    try {
      const registrationRef = doc(db, 'registrations', registrationId);
      await updateDoc(registrationRef, {
        status: newStatus,
        updatedAt: serverTimestamp()
      });

      // Update local state
      setRegistrations(prev => 
        prev.map(reg => 
          reg.id === registrationId 
            ? { ...reg, status: newStatus }
            : reg
        )
      );
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  // Handle registration deletion
  const handleDeleteRegistration = async () => {
    if (!registrationToDelete) return;

    try {
      await deleteDoc(doc(db, 'registrations', registrationToDelete.id));
      
      // Update local state
      setRegistrations(prev => 
        prev.filter(reg => reg.id !== registrationToDelete.id)
      );
      
      setShowDeleteModal(false);
      setRegistrationToDelete(null);
    } catch (error) {
      console.error('Error deleting registration:', error);
    }
  };

  // Handle sort
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const hasActiveFilters =
    searchTerm !== '' ||
    statusFilter !== 'all' ||
    typeFilter !== 'all' ||
    sortField !== 'date' ||
    sortDirection !== 'desc';

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setTypeFilter('all');
    setSortField('date');
    setSortDirection('desc');
  };
  const getStatusBadgeVariant = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'warning';
      case 'assigned':
        return 'success';
      case 'hold':
        return 'info';
      case 'cancelled':
        return 'danger';
      case 'submitted':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  // Get type display name
  const getTypeDisplayName = (type: number) => {
    const typeMap: { [key: number]: string } = {
      1: '1 - Program Staff',
      2: '2 - Returning (Same Spot)',
      3: '3 - Returning (No Fixed)',
      4: '4 - Returning (Different)',
      5: '5 - New Camper',
      6: '6 - Private Cottage',
      7: '7 - Off Grounds'
    };
    return typeMap[type] || 'Unknown';
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-2">
            <h2 className="mb-0">
              {isArchiveView ? `Archive: ${viewYear} Registrations` : 'Accommodation Assignment'}
            </h2>
            {!isArchiveView && (
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => navigate('/admin/reports', { state: { openReport: 'etransferInbox' } })}
                >
                  eTransfer Inbox
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => navigate('/admin/assignments/manual-registration')}
                >
                  Manual Registration
                </button>
              </div>
            )}
          </div>

          {isArchiveView && (
            <div style={{
              backgroundColor: '#fff3cd',
              border: '2px solid #ffc107',
              borderRadius: '8px',
              padding: '0.75rem 1.25rem',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <strong style={{ color: '#856404' }}>Read-Only Archive View</strong>
              <span style={{ color: '#856404' }}>— Viewing historical data from {viewYear}. No changes can be made.</span>
            </div>
          )}
          
          {/* Search and Filters */}
          <div className="assignment-controls">
            <div className="search-filters">
              <select
                className="filter-select"
                value={viewYear}
                onChange={(e) => {
                  setViewYear(e.target.value);
                  setSearchTerm('');
                  setStatusFilter('all');
                  setTypeFilter('all');
                }}
              >
                <option value="current">Current Year</option>
                {availableArchiveYears.map(year => (
                  <option key={year} value={String(year)}>{year} Archive</option>
                ))}
              </select>
              <input
                type="text"
                className="search-input"
                placeholder="Search registrations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <select
                className="filter-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="assigned">Assigned</option>
                <option value="hold">Hold</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <select
                className="filter-select"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <option value="all">All Types</option>
                <option value="1">Program Leader</option>
                <option value="2">Adult</option>
                <option value="3">Adult Student</option>
                <option value="4">Youth/Children</option>
                <option value="5">Cottage</option>
                <option value="6">Apartment</option>
                <option value="7">Private Cottage</option>
                <option value="8">Camping</option>
              </select>
              <button
                type="button"
                className="btn btn-outline-secondary clear-filters-btn"
                onClick={clearFilters}
                disabled={!hasActiveFilters}
              >
                Clear Filters
              </button>
            </div>
          </div>

          {/* Summary Cards — only for current year */}
          {!isArchiveView && (
            <div className="accommodation-stats mb-4">
              <div className="stat-card">
                <h3>Pending Assignments</h3>
                <div className="stat-number">{registrations.filter(r => (r.status || '').toLowerCase() === 'pending').length}</div>
                <button 
                  className="btn btn-secondary btn-sm"
                  onClick={() => setStatusFilter('pending')}
                >
                  Click to Filter
                </button>
              </div>
              <div className="stat-card">
                <h3>Assigned</h3>
                <div className="stat-number">{registrations.filter(r => (r.status || '').toLowerCase() === 'assigned').length}</div>
                <button 
                  className="btn btn-secondary btn-sm"
                  onClick={() => setStatusFilter('assigned')}
                >
                  Click to Filter
                </button>
              </div>
              <div className="stat-card">
                <h3>Hold</h3>
                <div className="stat-number">{registrations.filter(r => (r.status || '').toLowerCase() === 'hold').length}</div>
                <button 
                  className="btn btn-secondary btn-sm"
                  onClick={() => setStatusFilter('hold')}
                >
                  Click to Filter
                </button>
              </div>
            </div>
          )}

          <div className="mb-3">
            <strong>Showing {filteredAndSortedRegistrations.length} registrations{isArchiveView ? ` from ${viewYear}` : ''}</strong>
          </div>

          {/* Registrations Table */}
          <div className="registrations-table-container">
            <table className="registrations-table">
                <thead>
                  <tr>
                    <th 
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('type')}
                    >
                      Type {sortField === 'type' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('name')}
                    >
                      Name {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('accommodation')}
                    >
                      Accommodation {sortField === 'accommodation' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('campers')}
                    >
                      # Campers {sortField === 'campers' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('date')}
                    >
                      Date {sortField === 'date' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th>Status</th>
                    <th
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('balance')}
                    >
                      Balance Owing {sortField === 'balance' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedRegistrations.map((registration) => (
                    <tr 
                      key={registration.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        const currentViewParams = new URLSearchParams();
                        if (searchTerm) currentViewParams.set('q', searchTerm);
                        if (statusFilter !== 'all') currentViewParams.set('status', statusFilter);
                        if (typeFilter !== 'all') currentViewParams.set('type', typeFilter);
                        if (viewYear !== 'current') currentViewParams.set('view', viewYear);
                        if (sortField !== 'date') currentViewParams.set('sortField', sortField);
                        if (sortDirection !== 'desc') currentViewParams.set('sortDirection', sortDirection);
                        const currentViewQuery = currentViewParams.toString();

                        const detailsParams = new URLSearchParams();
                        if (isArchiveView) {
                          detailsParams.set('archive', viewYear);
                        }
                        detailsParams.set('returnTo', `/admin/assignments${currentViewQuery ? `?${currentViewQuery}` : ''}`);
                        navigate(`/admin/registration-details/${registration.id}?${detailsParams.toString()}`);
                      }}
                    >
                      <td>{registration.type}</td>
                      <td>{(registration.firstName || '')} {(registration.lastName || '')}</td>
                      <td>
                        {registration.assignedAccommodation ? (
                          <div>
                            <strong>{registration.assignedAccommodation.placeName}</strong>
                            <br />
                            <small className="text-muted">{registration.assignedAccommodation.placeCode}</small>
                          </div>
                        ) : (
                          <span className="text-muted">{registration.accommodationType || 'Not assigned'}</span>
                        )}
                      </td>
                      <td>{registration.attendingCampers.length}</td>
                      <td>
                        {registration.submittedAt && (
                          <div>
                            {format(new Date(registration.submittedAt), 'MMM dd, yyyy')}
                          </div>
                        )}
                      </td>
                      <td>
                        <span className={`status-badge status-${registration.status.toLowerCase()}`}>
                          {registration.status.toUpperCase()}
                        </span>
                      </td>
                      <td>
                        {registration.status?.toLowerCase() === 'pending'
                          ? (0).toLocaleString('en-CA', { style: 'currency', currency: 'CAD' })
                          : campSettings
                            ? computeRegistrationBalance((registration as any).originalData || registration, campSettings)
                                .toLocaleString('en-CA', { style: 'currency', currency: 'CAD' })
                            : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
            </table>
            
            {filteredAndSortedRegistrations.length === 0 && (
              <div className="text-center py-4">
                <p className="text-muted">No registrations found matching your criteria.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Assignment Modal */}
      <Modal show={showAssignmentModal} onHide={() => setShowAssignmentModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Assign Accommodation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRegistration && (
            <div>
              <h5>{(selectedRegistration.firstName || '')} {(selectedRegistration.lastName || '')}</h5>
              <p><strong>Type:</strong> {getTypeDisplayName(selectedRegistration.type)}</p>
              <p><strong>Requested:</strong> {selectedRegistration.accommodationType}</p>
              <p><strong>Nights:</strong> {selectedRegistration.stayDetails?.numberOfNights || 0}</p>
              
              <Form.Group className="mb-3">
                <Form.Label>Select Accommodation</Form.Label>
                <Form.Select
                  value={selectedAccommodation}
                  onChange={(e) => {
                    setSelectedAccommodation(e.target.value);
                    const accommodation = accommodations.find(acc => acc.placeCode === e.target.value);
                    setPlaceNameDisplay(accommodation?.placeName || '');
                  }}
                >
                  <option value="">Choose accommodation...</option>
                  {accommodations
                    .filter(acc => acc.type === selectedRegistration.accommodationType)
                    .map(accommodation => (
                      <option key={accommodation.id} value={accommodation.placeCode}>
                        {accommodation.placeName} ({accommodation.placeCode})
                      </option>
                    ))}
                </Form.Select>
              </Form.Group>
              
              {selectedAccommodation && (
                <div className="alert alert-info">
                  <strong>Selected:</strong> {placeNameDisplay} ({selectedAccommodation})
                  <br />
                  <strong>Estimated Fee:</strong> {formatCurrency(
                    getAccommodationFee(
                      accommodations.find(acc => acc.placeCode === selectedAccommodation)!,
                      selectedRegistration.stayDetails?.numberOfNights || 0
                    )
                  )}
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAssignmentModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleAssignAccommodation}
            disabled={!selectedAccommodation}
          >
            Assign Accommodation
          </Button>
        </Modal.Footer>
      </Modal>


      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the registration for {(registrationToDelete?.firstName || '')} {(registrationToDelete?.lastName || '')}? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteRegistration}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
};

export default Assignment;
